// API-Helpers fuer das Cockpit-Frontend.
//
// - fetch immer mit credentials: "include" (Auth-Cookie ist HttpOnly+Secure).
// - 401 → Redirect zu /login (signal an QueryClient).
// - Decimal-Felder kommen als String aus dem Backend; Frontend rendert
//   sie als String (kein parseFloat fuer Geld!).

export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const detail = await res
      .json()
      .then((b) => (typeof b?.detail === "string" ? b.detail : `HTTP ${res.status}`))
      .catch(() => `HTTP ${res.status}`);
    throw new Error(detail);
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// ---- KPI-Schemas (Sprint 2) ----

export type KpiStatus = "ok" | "warn" | "crit" | "no_data";

export interface SparkPoint {
  date: string;
  value: number;
}

export interface CashTotalKpi {
  total_eur: string;
  delta_vs_yesterday_eur: string | null;
  sparkline_14d: SparkPoint[];
  status: KpiStatus;
  tax_cap_warning?: TaxCapWarning | null;
  narrative?: string | null;
}

export interface RunwayKpi {
  runway_days: number | null;
  monthly_burn_eur: string | null;
  sparkline_14d: SparkPoint[];
  status: KpiStatus;
  narrative?: string | null;
}

export interface DsoKpi {
  dso_days: number | null;
  delta_vs_last_month: number | null;
  sparkline_14d: SparkPoint[];
  status: KpiStatus;
  narrative?: string | null;
}

export interface DpoKpi {
  dpo_days: number | null;
  delta_vs_last_month: number | null;
  sparkline_14d: SparkPoint[];
  status: KpiStatus;
  narrative?: string | null;
}

export interface ForecastComponent {
  art: string;
  label: string;
  betrag_eur: string;
}

export interface ForecastWeekPoint {
  week_start: string;
  end_saldo: number;
  abfluesse: number;
  zufluesse: number;
  // Patch 2 — Hybrid-Trichter:
  p10: number | null;
  p50: number | null;
  p90: number | null;
  is_deterministic: boolean;
  components: ForecastComponent[];
}

export type ForecastMode = "kontostand" | "delta";

export interface Forecast13wKpi {
  series: ForecastWeekPoint[];
  end_saldo_eur: string | null;
  end_saldo_p50_eur: string | null;
  status: KpiStatus;
  next_tax_marker: NextTaxMarker | null;
  narrative?: string | null;
  // Phase F (Sprint 7) — Hero-Tile-Aggregate
  mode: ForecastMode;
  bank_saldo_start_eur: string | null;
  total_abfluesse_eur: string;
  total_zufluesse_eur: string;
  net_movement_eur: string;
  confidence_band_pct: number;
}

// Phase F.2 — Liquiditaets-Planung Endpoint
export type LiquiSeverity = "overdue" | "urgent" | "soon" | "scheduled";

export interface LiquiPlanungTermin {
  firma_id: HoldingFirma;
  firma_display: string;
  art_display: string;
  datum: string;
  betrag_eur: string;
  days_until: number;
  severity: LiquiSeverity;
  headline: string;
}

export interface LiquiPlanungResponse {
  next_critical_termine: LiquiPlanungTermin[];
  monats_burn_rate_eur: string;
  empfohlene_mindest_liquiditaet_eur: string;
  aktueller_cash_konsolidiert_eur: string | null;
  // Cash-Graph (K7): cash → Deckungsgrad der 3-Monats-Reserve + Reichweite.
  deckung_pct: number | null;
  reichweite_tage: number | null;
  burn_recurring_eur: string;
  burn_steuer_avg_eur: string;
  notiz: string | null;
}

export interface TaxCapWarning {
  upcoming_30d_eur: string;
  cash_eur: string;
  deficit_eur: string;
  coverage_ratio: number;
  triggered: boolean;
}

export type HoldingFirma = "drainx" | "gundk" | "xholding" | "infrax";

export interface EntityUmsatzSource {
  betrag_eur: string;
  source: "tooltime_live" | "bwa";
  age_hours: number | null;
  period_label: string | null;
}

export interface EntityForderungOverdueRow {
  kunde: string;
  betrag_brutto_eur: string;
  tage_ueberfaellig: number;
  mahnstufe: number;
}

export interface EntityAgingBuckets {
  b0_30_count: number;
  b0_30_eur: string;
  b30_60_count: number;
  b30_60_eur: string;
  b60_90_count: number;
  b60_90_eur: string;
  b90_plus_count: number;
  b90_plus_eur: string;
}

export interface EntityMahnstufenVerteilung {
  stufe_0: number;
  stufe_1: number;
  stufe_2: number;
  stufe_3: number;
}

export interface EntityKpi {
  firma: HoldingFirma;
  firma_display: string;
  saldo_eur: string | null;
  dso_days: number | null;
  dpo_days: number | null;
  pipeline_eur: string | null;
  forderungen_eur: string | null;
  forderungen_netto_eur: string | null;
  mitarbeiter_aktiv: number | null;
  mitarbeiter_total: number | null;
  status: KpiStatus;
  // Sprint 8 — Detail-Page-Parity (T1/T2/T3).
  forderungen_offen_count: number | null;
  forderungen_ueberfaellig_eur: string | null;
  forderungen_ueberfaellig_count: number | null;
  umsatz_primary: EntityUmsatzSource | null;
  umsatz_secondary: EntityUmsatzSource | null;
  aging_buckets: EntityAgingBuckets | null;
  top_overdue: EntityForderungOverdueRow[] | null;
  mahnstufen_verteilung: EntityMahnstufenVerteilung | null;
}

export interface SyncStatusKpi {
  konten_count: number;
  konten_aktuell_count: number;
  oldest_sync_age_hours: number | null;
  status: "no_konten" | "ok" | "stale";
  message: string;
}

export const fetchKpiCashTotal = () =>
  apiFetch<CashTotalKpi>("/api/v1/kpi/cash-total");
export const fetchKpiRunway = () => apiFetch<RunwayKpi>("/api/v1/kpi/runway");
export const fetchKpiDso = () => apiFetch<DsoKpi>("/api/v1/kpi/dso");
export const fetchKpiDpo = () => apiFetch<DpoKpi>("/api/v1/kpi/dpo");
export const fetchKpiForecast13w = () =>
  apiFetch<Forecast13wKpi>("/api/v1/kpi/forecast-13w");
export const fetchKpiEntity = (firma: HoldingFirma) =>
  apiFetch<EntityKpi>(`/api/v1/kpi/entity/${firma}`);
export const fetchKpiSyncStatus = () =>
  apiFetch<SyncStatusKpi>("/api/v1/kpi/sync-status");
export const fetchKpiLiquiPlanung = () =>
  apiFetch<LiquiPlanungResponse>("/api/v1/kpi/liqui-planung");

// ---- Task 2 (2026-05-20): Aging-basierter 13W-Incoming-Forecast ----
// scope='konzern' = drainx+gundk+xholding (R1, OHNE infrax).
// scope='infrax' = isolierter Datenraum (fuer /firmen/infrax-Tile).

export type LiquiScope = "konzern" | "drainx" | "gundk" | "infrax";

export interface LiquiWoche {
  woche: number;
  datum: string;
  eingang_eur: string;
  ausgang_eur: string;
  netto_eur: string;
}

export interface LiquiSummary {
  total_eingang_eur: string;
  total_ausgang_eur: string;
  netto_13w_eur: string;
}

export interface LiquiAnnahmen {
  P_0_30: number;
  P_30_60: number;
  P_60_90: number;
  P_90_plus: number;
  horizon_wochen: number;
}

export interface LiquiForecastResponse {
  scope: LiquiScope;
  snapshot_iso: string | null;
  wochen: LiquiWoche[];
  summary: LiquiSummary;
  annahmen: LiquiAnnahmen;
  notiz: string | null;
}

export const fetchLiquiForecast13w = (scope: LiquiScope = "konzern") =>
  apiFetch<LiquiForecastResponse>(`/api/v1/liqui/forecast-13w/${scope}`);

// ---- Forderungen-Detail (Sprint 8 T3) -------------------------------
// Per-Firma Aging/Top-5/Mahnstufen aus Tooltime-Snapshot. cents int auf
// der Wire — Frontend formatiert mit cents/100. R1: alle 4 Firmen erlaubt,
// aber KEINE Konzern-Aggregate aus diesen Endpoints.

export type FirmaSlugAny = HoldingFirma | "infrax";

export interface AgingBucketsResponse {
  firma: FirmaSlugAny;
  status: "ok" | "no_data";
  "0_30_cents": number | null;
  "30_60_cents": number | null;
  "60_90_cents": number | null;
  "90_plus_cents": number | null;
  snapshot_iso: string | null;
  message: string | null;
}

export interface TopOverdueEntryDto {
  number: string | null;
  customer: string | null;
  betrag_brutto_cents: number | null;
  tage_ueberfaellig: number | null;
  dunning_state: string | null;
}

export interface TopOverdueResponse {
  firma: FirmaSlugAny;
  status: "ok" | "no_data";
  entries: TopOverdueEntryDto[];
  snapshot_iso: string | null;
  message: string | null;
}

export interface MahnstufenCountsResponse {
  firma: FirmaSlugAny;
  status: "ok" | "no_data";
  none_count: number;
  payment_reminder: number;
  level_one_dunning: number;
  level_two_dunning: number;
  total: number;
  snapshot_iso: string | null;
  message: string | null;
}

export const fetchAgingBuckets = (firma: FirmaSlugAny) =>
  apiFetch<AgingBucketsResponse>(`/api/v1/forderungen/aging/${firma}`);

export const fetchTopOverdue = (firma: FirmaSlugAny) =>
  apiFetch<TopOverdueResponse>(`/api/v1/forderungen/top-overdue/${firma}`);

export const fetchMahnstufen = (firma: FirmaSlugAny) =>
  apiFetch<MahnstufenCountsResponse>(`/api/v1/forderungen/mahnstufen/${firma}`);

// ---- Sync-Status (Patch 5, Sprint 2 Tag 10) ----

export type SyncState = "no_accounts" | "fresh" | "stale";

export interface SyncStatus {
  state: SyncState;
  konten_count: number;
  last_sync_at: string | null;
  last_sync_age_seconds: number | null;
  message: string;
  cta_label: string | null;
  cta_href: string | null;
}

export const fetchSyncStatus = () => apiFetch<SyncStatus>("/api/v1/sync/status");

// ---- Steuer-Termine (Sprint 2 Tag 13/15) ----

export type SteuerSeverity = "overdue" | "urgent" | "soon" | "scheduled";

export interface SteuerTerminEntry {
  id: string;
  firma_id: HoldingFirma;
  firma_display: string;
  art: string;
  art_display: string;
  datum: string; // ISO YYYY-MM-DD
  betrag_eur: string; // Decimal
  quelle: string;
  notiz: string | null;
  days_until: number;
  severity: SteuerSeverity;
  created_at: string;
  updated_at: string;
}

export interface SteuerUpcomingResponse {
  available: boolean;
  as_of_date: string;
  horizon_days: number;
  n_total: number;
  sum_eur: string;
  by_firma: Record<string, string>;
  entries: SteuerTerminEntry[];
}

export interface ManualSteuerTerminRequest {
  firma_id: HoldingFirma;
  art: string;
  datum: string;
  betrag_eur: number;
  notiz?: string | null;
}

export interface ManualSteuerTerminResponse {
  id: string;
  persisted: boolean;
}

export interface NextTaxMarker {
  firma_id: HoldingFirma;
  firma_display: string;
  art: string;
  art_display: string;
  datum: string;
  betrag_eur: string;
  days_until: number;
  headline: string;
}

export const fetchSteuerUpcoming = (params?: {
  horizon_days?: number;
  firma?: HoldingFirma;
  include_overdue?: boolean;
}) => {
  const q = new URLSearchParams();
  if (params?.horizon_days) q.set("horizon_days", String(params.horizon_days));
  if (params?.firma) q.set("firma", params.firma);
  if (params?.include_overdue !== undefined)
    q.set("include_overdue", String(params.include_overdue));
  const qs = q.toString();
  return apiFetch<SteuerUpcomingResponse>(
    `/api/v1/steuern/upcoming${qs ? `?${qs}` : ""}`,
  );
};

export const postManualSteuer = (req: ManualSteuerTerminRequest) =>
  apiFetch<ManualSteuerTerminResponse>("/api/v1/steuern/manual", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });

export const deleteSteuerTermin = (firma: HoldingFirma, id: string) =>
  apiFetch<void>(`/api/v1/steuern/${firma}/${id}`, { method: "DELETE" });

// ---- Recurring-Outflow-Patterns (Migration 0008) ----

export type RecurringKategorie =
  | "personal_brutto"
  | "personal_lst_sv"
  | "material_einkauf"
  | "versicherungen"
  | "fahrzeuge_leasing"
  | "raum_miete"
  | "werbe_reise"
  | "reparatur"
  | "sonstige"
  | "zinsaufwand";

export interface RecurringPatternEntry {
  id: string;
  firma_id: HoldingFirma;
  firma_display: string;
  kategorie: RecurringKategorie;
  kategorie_display: string;
  kategorie_color: string;
  betrag_monatlich_eur: string;
  tag_im_monat: number;
  source: string;
  notiz: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FirmaBurnBreakdown {
  firma_id: HoldingFirma;
  firma_display: string;
  total_monatlich_eur: string;
  by_kategorie: RecurringPatternEntry[];
}

export interface BurnRateResponse {
  available: boolean;
  total_monatlich_eur: string;
  by_firma: FirmaBurnBreakdown[];
  notiz: string | null;
  /** T1 (#54, 2026-05-21): BWA-Anker-Datum + Alter fuer StaleBadge.
   *  recurring_outflow_pattern.source ist statisch — BWA-Anker zeigt
   *  den ehrlichen Stand der zugrunde liegenden Periode. */
  bwa_anchor_iso: string | null;
  bwa_anchor_age_days: number | null;
}

export const fetchRecurringPatterns = (params?: {
  firma?: HoldingFirma;
  only_active?: boolean;
}) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.only_active !== undefined)
    q.set("only_active", String(params.only_active));
  const qs = q.toString();
  return apiFetch<RecurringPatternEntry[]>(
    `/api/v1/recurring/patterns${qs ? `?${qs}` : ""}`,
  );
};

export const fetchBurnRate = () =>
  apiFetch<BurnRateResponse>("/api/v1/recurring/burn-rate");

// ---- Schemas (entsprechen den Pydantic-Models im Backend) ----

export interface FirmaCard {
  firma_id: string;
  firma_name: string | null;
  balance_eur: string; // Decimal als String
  delta_eur: string | null;
  konten_count: number;
  latest_snapshot_date: string | null;
  opex_yearly_eur: string | null;
  days_cash_on_hand: number | null;
  liquidity_status: "green" | "yellow" | "red" | "unknown";
}

export interface ConsolidatedHolding {
  balance_eur: string;
  delta_eur: string | null;
  firmas_included: string[];
}

export interface StaleWarning {
  firma_id: string;
  bank_konto_id: string;
  bezeichnung: string | null;
  last_synced_at: string | null;
  age_hours: number;
}

export interface TodayResponse {
  as_of_date: string;
  consolidated: ConsolidatedHolding;
  per_firma: FirmaCard[];
  stale_warnings: StaleWarning[];
  has_aggregator_role: boolean;
}

export interface CashForecastEntry {
  grosshaendler: string;
  grosshaendler_display: string;
  outflow_date: string;
  betrag_eur: string | null;
  rechnungsnummer: string | null;
  confidence: number;
}

export interface CashForecastBucket {
  label: string;
  sum_eur: string;
  n_invoices: number;
  entries: CashForecastEntry[];
}

export interface CashForecastResponse {
  available: boolean;
  as_of_date: string;
  today: CashForecastBucket;
  next_7_days: CashForecastBucket;
  next_30_days: CashForecastBucket;
  total_sum_eur: string;
  n_total: number;
  setup_hint: string | null;
}

export interface MeResponse {
  email: string;
  exp: number;
  totp_verified: boolean;
  /** AUFTRAG E: Passkey-Session (30 T) — volle Session ohne TOTP-Claim. */
  webauthn_verified?: boolean;
  totp_state: "none" | "pending" | "confirmed";
}

// ---- Convenience-Wrappers ----

export const fetchMe = () => apiFetch<MeResponse>("/api/v1/auth/me");
export const fetchToday = () => apiFetch<TodayResponse>("/api/v1/cockpit/today");
export const fetchCashForecast = () =>
  apiFetch<CashForecastResponse>("/api/v1/cockpit/cash-forecast");

// ---- Vacations ----

export interface VacationEntry {
  name: string;
  firma: string;
  start_iso: string;
  end_iso: string;
  days_until_start: number | null;
}

export interface BirthdayEntry {
  name: string;
  iso: string;
  days_until: number;
}

export interface VacationsResponse {
  available: boolean;
  today: string;
  today_off: VacationEntry[];
  this_week: VacationEntry[];
  coming_2_weeks: VacationEntry[];
  birthdays_14d: BirthdayEntry[];
  setup_hint: string | null;
  stale: boolean;
}

export const fetchVacations = () => apiFetch<VacationsResponse>("/api/v1/cockpit/vacations");

// ---- Today-Briefing ("Heute relevant"-Card) ----

export type Bucket = "heute_kritisch" | "diese_woche" | "nachverfolgen";

export interface BriefingEntry {
  bucket: Bucket;
  firma_tag: string;
  mailbox_slug: string;
  from_address: string;
  from_name: string | null;
  subject: string;
  received_at: string;
  summary: string;
  action_hint: string | null;
  gmail_deeplink: string | null;
}

export interface TodayBriefingResponse {
  available: boolean;
  run_date: string | null;
  heute_kritisch: BriefingEntry[];
  diese_woche: BriefingEntry[];
  nachverfolgen: BriefingEntry[];
  setup_hint: string | null;
}

export const fetchTodayBriefing = () =>
  apiFetch<TodayBriefingResponse>("/api/v1/cockpit/today-briefing");

// ---- Forderungen DrainX ----

export interface ForderungEntry {
  project_number: string;
  project_name: string;
  customer_name: string | null;
  received_date: string | null;
  days_since_update: number;
  /** T2.13-C: Project-age (Tage seit received_date). None wenn
   * received_date unbekannt — Frontend faellt dann auf days_since_update
   * zurueck. */
  days_since_received: number | null;
}

export interface ForderungenResponse {
  available: boolean;
  n_total_open: number;
  n_overdue: number;
  n_stale_filtered: number;
  top_n: ForderungEntry[];
  setup_hint: string | null;
  stale: boolean;
}

export const fetchForderungen = () =>
  apiFetch<ForderungenResponse>("/api/v1/cockpit/forderungen");

// ---- Outflow-Forecast (pro Firma) ----

export interface OutflowEntry {
  firma: string;
  firma_display: string;
  category: string;
  category_display: string;
  category_color: string;
  vendor_name: string;
  outflow_date: string;
  betrag_eur: string | null;
  rechnungsnummer: string | null;
  confidence: number;
}

export interface OutflowCategoryStat {
  category: string;
  category_display: string;
  category_color: string;
  sum_eur: string;
  n_invoices: number;
}

export interface OutflowDayRow {
  date: string;
  label: string;
  total: number;
  [k: string]: string | number;
}

export interface FirmaBlock {
  firma: string;
  firma_display: string;
  total_sum_eur: string;
  n_total: number;
  today_sum_eur: string;
  next_7_sum_eur: string;
  next_30_sum_eur: string;
  next_28_sum_eur: string;
  next_28_salary_sum_eur: string;
  weekly_buckets: WeeklyBucket[];
  by_category: OutflowCategoryStat[];
  upcoming: OutflowEntry[];
}

export interface WeeklyBucket {
  week_index: number;
  start_iso: string;
  end_iso: string;
  sum_eur: string;
  n_total: number;
}

export interface OutflowForecastResponse {
  available: boolean;
  as_of_date: string;
  total_sum_eur: string;
  n_total: number;
  today_sum_eur: string;
  next_7_sum_eur: string;
  next_30_sum_eur: string;
  next_28_sum_eur: string;
  next_28_salary_sum_eur: string;
  weekly_buckets: WeeklyBucket[];
  by_day: OutflowDayRow[];
  firmas: FirmaBlock[];
  setup_hint: string | null;
}

export const fetchOutflowForecast = () =>
  apiFetch<OutflowForecastResponse>("/api/v1/cockpit/outflow-forecast");

// ---- Tooltime-Live-Umsatz (Sprint-7-Push 4) ---------------------
// Cowork-Schedule scraped wochentlich die Tooltime-Dashboards (REST-API
// liefert keine Betraege) und POSTet einen Snapshot — Cockpit liest ihn
// hier zurueck.

export interface TooltimeFirmaSnapshot {
  firma_id: "drainx" | "gundk" | "infrax" | "xholding";
  umsatz_ytd_netto_eur: string | null;
  umsatz_mtd_netto_eur: string | null;
  forderungen_offen_brutto_eur: string | null;
  forderungen_offen_netto_eur: string | null;
  forderungen_offen_count: number | null;
  forderungen_ueberfaellig_brutto_eur: string | null;
  forderungen_ueberfaellig_netto_eur: string | null;
  warnings: string[];
}

export interface UmsatzLiveResponse {
  available: boolean;
  snapshot_iso: string | null;
  year: number | null;
  age_hours: number | null;
  firmas: TooltimeFirmaSnapshot[];
  konzern_umsatz_ytd_netto_eur: string | null;
  setup_hint: string | null;
  /** T6 (#46, 2026-05-21): true wenn juengster Snapshot aus bootstrap_seed
   *  kommt — kein echter Cowork-Pull. Frontend zeigt warn-StaleBadge
   *  wenn age_hours>24 UND is_bootstrap_only=true (Cowork-Cron haengt). */
  is_bootstrap_only?: boolean;
  latest_source?: string | null;
}

export const fetchUmsatzLive = () =>
  apiFetch<UmsatzLiveResponse>("/api/v1/kpi/umsatz-live");

// ---- Termine 14d-Aggregator (T2.12) ----

export type TerminSource = "steuer" | "grosshaendler" | "outflow";
export type TerminSeverity = "overdue" | "urgent" | "soon";

export interface TerminEntry {
  source: TerminSource;
  firma: string;
  firma_display: string;
  datum: string; // ISO YYYY-MM-DD
  betrag_eur: string | null; // Decimal als String
  label: string;
  days_until: number;
  severity: TerminSeverity;
  category: string | null;
}

export interface Termine14dResponse {
  available: boolean;
  as_of_date: string;
  entries: TerminEntry[];
  setup_hint: string | null;
}

export const fetchTermine14d = () =>
  apiFetch<Termine14dResponse>("/api/v1/cockpit/termine-14d");

// ---- T2.16b/f Pipelines (Decision-Queue / Pending / Signals / Aging) ----

export interface DecisionQueueItem {
  id: string;
  firma_id: string;
  firma_display: string;
  typ: string;
  titel: string;
  beschreibung: string | null;
  vorgeschlagene_aktion: string | null;
  status: string;
  source: string;
  external_reference: string | null;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  // Tag 18 — Tooltime-Writer-Felder
  approved_at: string | null;
  approved_by: string | null;
  tooltime_action_type: string | null;
  tooltime_customer_id: number | null;
  tooltime_project_id: number | null;
  tooltime_error: string | null;
}

export interface DecisionQueueListResponse {
  items: DecisionQueueItem[];
  total: number;
}

export interface DecisionQueueCountResponse {
  count: number;
  oldest_iso: string | null;
}

export interface PendingVerbindlichkeitItem {
  id: string;
  firma_id: string;
  firma_display: string;
  vendor_name: string;
  betrag_eur: string;
  faelligkeit: string | null;
  kategorie: string | null;
  status: string;
  week_offset: number | null;
  /** Bug A3 (#53): wenn Aggregator-Pipeline-Duplikate gemerged wurden,
   *  zeigt das Frontend "× N" + Tooltip-Hinweis. 1 = unique. */
  duplicate_count: number;
}

export interface PendingVerbindlichkeitenResponse {
  items: PendingVerbindlichkeitItem[];
  total_eur: string;
  n_total: number;
}

export interface PipelineSignalItem {
  id: string;
  firma_id: string;
  firma_display: string;
  kategorie: string;
  titel: string;
  beschreibung: string | null;
  betrag_eur: string | null;
  erwartetes_datum: string | null;
  status: string;
  created_at: string;
}

export interface PipelineSignalsResponse {
  items: PipelineSignalItem[];
}

export interface ForderungsAgingEventItem {
  id: string;
  firma_id: string;
  firma_display: string;
  forderung_ref: string;
  event_typ: string;
  days_overdue: number | null;
  betrag_eur: string | null;
  empfehlung: string | null;
  status: string;
  created_at: string;
}

export interface ForderungsAgingEventsResponse {
  items: ForderungsAgingEventItem[];
}

export interface DecisionActionResponse {
  id: string;
  status: string;
  decided_at: string;
}

export const fetchDecisionQueue = (params?: { status?: string; limit?: number }) => {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<DecisionQueueListResponse>(
    `/api/v1/decision-queue${qs ? `?${qs}` : ""}`,
  );
};
export const fetchDecisionQueueCount = () =>
  apiFetch<DecisionQueueCountResponse>("/api/v1/decision-queue/count");

export const fetchPendingVerbindlichkeiten = (params?: {
  firma?: string;
  weeks?: number;
}) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.weeks) q.set("weeks", String(params.weeks));
  const qs = q.toString();
  return apiFetch<PendingVerbindlichkeitenResponse>(
    `/api/v1/pending-verbindlichkeiten${qs ? `?${qs}` : ""}`,
  );
};

export const fetchPipelineSignals = (limit = 20) =>
  apiFetch<PipelineSignalsResponse>(`/api/v1/pipeline-signals?limit=${limit}`);

export const fetchForderungsAgingEvents = (limit = 20) =>
  apiFetch<ForderungsAgingEventsResponse>(
    `/api/v1/forderungs-aging-events?limit=${limit}`,
  );

export type DecisionApproveAction = "tooltime_create" | "manual_only";

export const postDecisionApprove = (
  id: string,
  firma_id: string,
  action: DecisionApproveAction = "manual_only",
  note?: string,
) =>
  apiFetch<DecisionActionResponse>(`/api/v1/decision-queue/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ firma_id, note: note ?? null, action }),
  });

export const postDecisionRetryTooltime = (
  id: string,
  firma_id: string,
  note?: string,
) =>
  apiFetch<DecisionActionResponse>(
    `/api/v1/decision-queue/${id}/retry-tooltime`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ firma_id, note: note ?? null, action: "tooltime_create" }),
    },
  );

export const fetchDecisionItem = (id: string) =>
  apiFetch<DecisionQueueItem>(`/api/v1/decision-queue/${id}`);

// ---- Tag 19: Konzentrations-Radar DrainX ----

export type RisikoLevel = "niedrig" | "mittel" | "hoch";

export interface KonzentrationItem {
  kunde_name: string;
  n_offene_projekte: number;
  anteil_projekte_pct: number;
  forderungen_offen: number;
  umsatz_12m_eur: string | null;
}

export interface KonzentrationResponse {
  available: boolean;
  items: KonzentrationItem[];
  n_total_projekte: number;
  top_3_anteil_pct: number;
  konzentrations_risiko: RisikoLevel;
  umsatz_basis: "projekte_anzahl" | "ebics_eur";
  setup_hint: string | null;
}

export interface SzenarioResponse {
  kunde_name: string | null;
  cashflow_impact_eur: string | null;
  fixkosten_deckung_monate_neu: number | null;
  runway_neu_wochen: number | null;
  setup_hint: string | null;
}

export const fetchKonzentrationDrainx = () =>
  apiFetch<KonzentrationResponse>("/api/v1/konzentration/drainx");

export const fetchKonzentrationSzenario = (params: {
  kunde_id?: string | null;
  horizont_monate?: number;
}) => {
  const q = new URLSearchParams();
  if (params.kunde_id) q.set("kunde_id", params.kunde_id);
  if (params.horizont_monate) q.set("horizont_monate", String(params.horizont_monate));
  const qs = q.toString();
  return apiFetch<SzenarioResponse>(
    `/api/v1/konzentration/drainx/szenario${qs ? `?${qs}` : ""}`,
  );
};

// ---- T2.16b-EXTEND: BWA / Lohnlauf / USt-Voranmeldung ----

export interface BwaItem {
  id: string;
  firma_id: string;
  jahr: number;
  monat: number;
  // Monatsspalte (linke Pahl-Werte)
  umsatz_eur: string | null;
  aufwendungen_eur: string | null;
  ergebnis_eur: string | null;
  ebit_marge_pct: number | null;
  // YTD-Spalte (Jan-bis-aktueller-Monat, rechte Pahl-Werte). Mig 0026.
  gesamtleistung_eur_ytd: string | null;
  umsatz_eur_ytd: string | null;
  material_eur_ytd: string | null;
  personalkosten_eur_ytd: string | null;
  betriebsergebnis_eur_ytd: string | null;
  vorlaeufiges_ergebnis_eur_ytd: string | null;
  ebit_marge_pct_ytd: string | null;
  drive_file_id: string | null;
  source_filename: string;
  parsed_at: string | null;
}

export interface BwaResponse {
  items: BwaItem[];
}

export interface LohnlaufItem {
  id: string;
  firma_id: string;
  jahr: number;
  monat: number;
  anzahl_arbeitnehmer: number | null;
  brutto_eur: string | null;
  lohnsteuer_eur: string | null;
  soli_eur: string | null;
  sv_arbeitnehmer_eur: string | null;
  sv_arbeitgeber_eur: string | null;
  drive_file_id: string | null;
  source_filename: string;
}

export interface LohnlaufResponse {
  items: LohnlaufItem[];
}

export interface UstVoranmeldungItem {
  id: string;
  firma_id: string;
  jahr: number;
  monat: number;
  umsatzsteuer_eur: string | null;
  vorsteuer_eur: string | null;
  zahllast_eur: string | null;
  abgegeben_am: string | null;
  faellig_am: string | null;
  drive_file_id: string | null;
  source_filename: string;
}

export interface UstVoranmeldungResponse {
  items: UstVoranmeldungItem[];
}

export const fetchBwa = (params?: { firma?: string; jahr?: number; limit?: number }) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.jahr) q.set("jahr", String(params.jahr));
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<BwaResponse>(`/api/v1/bwa${qs ? `?${qs}` : ""}`);
};

export const fetchLohnlauf = (params?: {
  firma?: string;
  jahr?: number;
  limit?: number;
}) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.jahr) q.set("jahr", String(params.jahr));
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<LohnlaufResponse>(`/api/v1/lohnlauf${qs ? `?${qs}` : ""}`);
};

export const fetchUstVoranmeldung = (params?: {
  firma?: string;
  jahr?: number;
  limit?: number;
}) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.jahr) q.set("jahr", String(params.jahr));
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<UstVoranmeldungResponse>(
    `/api/v1/ust-voranmeldung${qs ? `?${qs}` : ""}`,
  );
};

// ---- Tag 20: Anomaly-Stream + Steuer-Compliance-90d ----

export type AnomalySeverity = "info" | "warn" | "critical";

export interface AnomalyEventItem {
  id: string;
  firma_id: string;
  kategorie: string;
  severity: AnomalySeverity;
  titel: string;
  payload: Record<string, unknown> | null;
  detected_at: string;
  dismissed_at: string | null;
  dismissed_by: string | null;
}

export interface AnomalyEventsResponse {
  items: AnomalyEventItem[];
  n_open: number;
  n_critical_open: number;
}

export const fetchAnomalyEvents = (params?: {
  firma?: string;
  severity?: AnomalySeverity;
  status?: "open" | "dismissed" | "all";
  limit?: number;
}) => {
  const q = new URLSearchParams();
  if (params?.firma) q.set("firma", params.firma);
  if (params?.severity) q.set("severity", params.severity);
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<AnomalyEventsResponse>(
    `/api/v1/anomaly-events${qs ? `?${qs}` : ""}`,
  );
};

export const dismissAnomalyEvent = (id: string, firma_id: string) =>
  apiFetch<{ id: string; dismissed_at: string }>(
    `/api/v1/anomaly-events/${id}/dismiss`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ firma_id }),
    },
  );

export type ComplianceTyp =
  | "steuer_zahlung"
  | "bescheinigung_ablauf"
  | "ust_va_eintrag";

export interface ComplianceItem {
  datum: string;
  firma_id: string;
  typ: ComplianceTyp;
  art: string;
  titel: string;
  betrag_eur: string | null;
  quelle: string | null;
  days_until: number;
}

export interface ComplianceResponse {
  items: ComplianceItem[];
  n_total: number;
  n_critical: number;
  /** Task 1: juengstes updated_at aus steuer_termin. */
  last_updated: string | null;
}

export const fetchSteuerCompliance90d = () =>
  apiFetch<ComplianceResponse>("/api/v1/steuer/compliance-90d");

// ---- Sprint 4 Push 1: BWA-Trend + Recurring-Write ----

export interface JaAnker {
  jahr: number;
  ebit_marge_pct: number;
}

export interface BwaTrendResponse {
  items: BwaItem[];
  ja_anker: JaAnker | null;
  /** Task 1: juengstes parsed_at aus den Items. */
  last_parsed_at: string | null;
}

export const fetchBwaTrend = (firma: string, monate = 12) =>
  apiFetch<BwaTrendResponse>(
    `/api/v1/bwa/trend/${firma}?monate=${monate}`,
  );

export interface RecurringPattern {
  id: string;
  firma_id: string;
  kategorie: string;
  betrag_monatlich_eur: string;
  tag_im_monat: number;
  source: string;
  notiz: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RecurringWritePayload {
  firma_id: string;
  kategorie: string;
  betrag_monatlich_eur: string;
  tag_im_monat: number;
  source: string;
  notiz: string | null;
  active: boolean;
}

export const createRecurring = (payload: RecurringWritePayload) =>
  apiFetch<{ id: string; updated_at: string }>(`/api/v1/recurring`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const updateRecurring = (id: string, payload: RecurringWritePayload) =>
  apiFetch<{ id: string; updated_at: string }>(
    `/api/v1/recurring/${id}/update`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

export const deleteRecurring = (id: string, firma_id: string) =>
  apiFetch<{ id: string; updated_at: string }>(`/api/v1/recurring/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ firma_id }),
  });

// ---- Sprint 4 Push 2: Outbound-Stubs ----

export interface MahnungOutboundItem {
  id: string;
  firma_id: string;
  forderung_id: string;
  kunde_name: string | null;
  telefon: string | null;
  betrag_eur: string | null;
  rg_nummer: string | null;
  mahnstufe: number | null;
  tage_seit_letzter_mahnung: number | null;
  status: string;
  dry_run: boolean;
  scheduled_for: string;
  called_at: string | null;
}

export interface MahnungOutboundResponse {
  items: MahnungOutboundItem[];
  n_pending: number;
  n_dry_run: number;
}

export const fetchMahnungOutbound = () =>
  apiFetch<MahnungOutboundResponse>("/api/v1/petra/mahnung-outbound?limit=20");

export interface TerminBestaetigungItem {
  id: string;
  firma_id: string;
  tooltime_termin_id: string;
  kunde_name: string | null;
  telefon: string | null;
  termin_datum: string;
  status: string;
  anti_spam_skip_reason: string | null;
  dry_run: boolean;
  scheduled_for: string;
}

export interface TerminBestaetigungResponse {
  items: TerminBestaetigungItem[];
  n_pending: number;
  n_skipped: number;
}

export const fetchTerminBestaetigung = () =>
  apiFetch<TerminBestaetigungResponse>(
    "/api/v1/petra/termin-bestaetigung?limit=20",
  );

// ---- Sprint 5 Push 2: Bürgschaft + Konzerndarlehen Drilldown ----

export interface BuergschaftHistoryEvent {
  operation: string;
  changed_at: string;
  changed_by: string;
}

export interface BuergschaftDetailResponse {
  item: BuergschaftItem;
  history: BuergschaftHistoryEvent[];
}

export const fetchBuergschaftDetail = (id: string) =>
  apiFetch<BuergschaftDetailResponse>(`/api/v1/buergschaft/detail/${id}`);

export interface TilgungsplanRate {
  rate: number;
  datum: string | null;
  betrag_eur: string | null;
  status: "offen" | "getilgt";
}

export interface TilgungsplanResponse {
  konzerndarlehen_id: string;
  glaeubiger_display: string;
  schuldner_display: string;
  gesamtbetrag_eur: string;
  bisher_getilgt_eur: string;
  rest_eur: string;
  naechste_rate_am: string | null;
  naechste_rate_eur: string | null;
  raten: TilgungsplanRate[];
}

export const fetchKonzerndarlehenTilgungsplan = (id: string) =>
  apiFetch<TilgungsplanResponse>(
    `/api/v1/konzerndarlehen/tilgungsplan/${id}`,
  );

export const postDecisionDefer = (id: string, firma_id: string, note?: string) =>
  apiFetch<DecisionActionResponse>(`/api/v1/decision-queue/${id}/defer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ firma_id, note: note ?? null }),
  });

// Manuelle Steuer-/Outflow-Eingabe
export interface ManualOutflowRequest {
  firma: "drainx" | "gundk" | "xholding";
  category:
    | "tax_ust"
    | "tax_kst"
    | "tax_gst"
    | "tax_lohn"
    | "tax_other"
    | "supplier"
    | "salary"
    | "utility"
    | "other";
  vendor_name: string;
  betrag_eur: number;
  faelligkeit: string;
  rechnungsnummer?: string | null;
  notiz?: string | null;
}

export interface ManualOutflowResponse {
  dedupe_id: string;
  persisted: boolean;
}

export const postManualOutflow = (req: ManualOutflowRequest) =>
  apiFetch<ManualOutflowResponse>("/api/v1/cockpit/outflow/manual", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });

// ---- Petra-Anrufe ----

export interface PetraCall {
  received_at: string;
  contact_name: string | null;
  phone_redacted: string | null;
  topic: string | null;
  summary: string | null;
  firma: "drainx" | "gundk" | "unknown";
  firma_confidence: "high" | "medium" | "low";
}

export interface PetraCallsResponse {
  available: boolean;
  n_today: number;
  n_unknown_today: number;
  n_yesterday: number;
  last_24h: PetraCall[];
  setup_hint: string | null;
}

export const fetchPetraCalls = () =>
  apiFetch<PetraCallsResponse>("/api/v1/cockpit/petra-calls");

// ---- Pipeline DrainX ----

export interface PipelineEntry {
  project_number: string;
  project_name: string;
  customer_name: string | null;
  received_date: string | null;
  days_since_received: number;
}

export interface PipelineResponse {
  available: boolean;
  n_offers_open: number;
  n_active_projects: number;
  n_offers_stale: number;
  top_offers: PipelineEntry[];
  setup_hint: string | null;
  stale: boolean;
}

export const fetchPipeline = () => apiFetch<PipelineResponse>("/api/v1/cockpit/pipeline");

// ---- TOTP ----

export interface TotpSetupResponse {
  provisioning_uri: string; // otpauth://totp/...
  backup_codes: string[];
}

export const totpSetup = () =>
  apiFetch<TotpSetupResponse>("/api/v1/auth/totp/setup", { method: "POST" });

export const totpConfirm = (code: string) =>
  apiFetch<void>("/api/v1/auth/totp/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });

export const totpVerify = (code: string) =>
  apiFetch<void>("/api/v1/auth/totp/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });

// ---- T2.16c Drive-Scanner-Reads ----

export interface BuergschaftItem {
  id: string;
  creditor_firma_id: string;
  creditor_display: string;
  debtor_firma_id: string;
  debtor_display: string;
  betrag_eur: string;
  sicherungsobjekt: string | null;
  laufzeit_bis: string | null;
  status: string;
  name: string;
}

export interface BuergschaftAggregateResponse {
  total_creditor_eur: string;
  n_creditor: number;
  items: BuergschaftItem[];
}

export interface KonzerndarlehenItem {
  id: string;
  glaeubiger_firma_id: string;
  schuldner_firma_id: string;
  schuldner_display: string;
  rate_nr: number | null;
  gesamtbetrag_eur: string;
  zinssatz_bps: number | null;
  naechste_rate_am: string | null;
  naechste_rate_eur: string | null;
  tilgungsplan: Array<{ rate: number; datum: string; betrag_eur: string }> | null;
  name: string;
}

export interface KonzerndarlehenResponse {
  items: KonzerndarlehenItem[];
}

export interface BescheinigungItem {
  id: string;
  firma_id: string;
  firma_display: string;
  typ: string;
  gueltig_von: string | null;
  gueltig_bis: string | null;
  status: string;
  days_until_expiry: number | null;
  name: string;
}

export interface BescheinigungenResponse {
  items: BescheinigungItem[];
  n_expired: number;
  n_expiring_soon: number;
}

export interface MahnungEingegangenItem {
  id: string;
  firma_id: string;
  firma_display: string;
  glaeubiger: string;
  betrag_eur: string;
  faellig_am: string | null;
  status: string;
  rechnungs_nr: string | null;
  name: string;
}

export interface MahnungenEingegangenResponse {
  items: MahnungEingegangenItem[];
  total_offen_eur: string;
  n_offen: number;
  /** Task 1 (2026-05-20): Cowork-Snapshot-Alter fuer Stale-Badge. */
  source_snapshot_iso: string | null;
}

export const fetchBuergschaft = () =>
  apiFetch<BuergschaftAggregateResponse>("/api/v1/buergschaft");
export const fetchKonzerndarlehen = () =>
  apiFetch<KonzerndarlehenResponse>("/api/v1/konzerndarlehen");
export const fetchBescheinigungen = (expiring_within_days = 30) =>
  apiFetch<BescheinigungenResponse>(
    `/api/v1/bescheinigungen?expiring_within_days=${expiring_within_days}`,
  );
export const fetchMahnungenEingegangen = () =>
  apiFetch<MahnungenEingegangenResponse>("/api/v1/mahnungen-eingegangen");

// ---- Jahresabschluss (Daten-Inventur 2026-07) ----

export interface JaEintrag {
  firma_id: string;
  jahr: number;
  gj_von: string | null;
  gj_bis: string | null;
  festgestellt_am: string | null;
  bilanzsumme_eur: string | null;
  eigenkapital_eur: string | null;
  rueckstellungen_eur: string | null;
  verbindlichkeiten_eur: string | null;
  umsatzerloese_eur: string | null;
  jahresueberschuss_eur: string | null;
  ek_quote_pct: string | null;
  quelle: string | null;
}

export interface JaResponse {
  status: "ok" | "no_data";
  eintraege: JaEintrag[];
  message: string | null;
}

export const fetchJa = () => apiFetch<JaResponse>("/api/v1/ja");

// ---- Fokus / Autosuggestion ----

export interface FokusZiel {
  jahr: number;
  titel: string;
  betrag_eur: number | null;
}

export interface FokusData {
  autosuggestion: string;
  ziele: FokusZiel[];
  countdown_datum: string;
}

export const fetchFokus = () => apiFetch<FokusData>("/api/v1/fokus");
export const putFokus = (data: FokusData) =>
  apiFetch<FokusData>("/api/v1/fokus", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

// ---- Format-Helpers ----

const _eurFormat = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatEUR(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return _eurFormat.format(n);
}

export function formatEURNegative(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n) || n === 0) return _eurFormat.format(0);
  return "−" + _eurFormat.format(Math.abs(n));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y.slice(2)}`;
}

/**
 * Days-Since-Formatter (T2.13-B konsistent zu Pipeline-Modul).
 * <365  -> "{n}d"
 * >=365 -> "{j} J [{m} Mo]"   (Mo weglassen wenn 0)
 *
 * Vorher: "1 J+" wenn restDays<30 — Cowork-Reverify hat das als
 * inkonsistent gegenueber Pipeline-Card markiert (#1 Sub-Finding).
 */
// ---- Team-Tab ----

export interface TeamLohnSummen {
  brutto_ytd_eur: string;
  netto_ytd_eur: string;
  brutto_mtd_eur: string;
  netto_mtd_eur: string;
}

export interface TeamTopEinzelposten {
  mitarbeiter_name: string;
  brutto_eur: string;
  monat: number;
}

// Sanierung 03.07.: Tooltime-Team-Block (2. Quelle neben Pahl-Lohn).
export interface TooltimeTeamTermin {
  datum_iso: string | null;
  titel: string | null;
  mitarbeiter: string | null;
  kunde: string | null;
  projekt: string | null;
}

export interface TooltimeTeamMitarbeiter {
  name: string;
  aktiv: boolean | null;
  rolle: string | null;
  termine_14d_count: number | null;
  erfasste_stunden_mtd: string | null;
  letzter_einsatz_iso: string | null;
}

export interface TooltimeTeamBlock {
  mitarbeiter: TooltimeTeamMitarbeiter[] | null;
  mitarbeiter_count: number | null;
  termine_next_14d: TooltimeTeamTermin[] | null;
  termine_next_14d_count: number | null;
  auftraege_offen_count: number | null;
  erfasste_stunden_mtd_total: string | null;
  warnings: string[];
}

// AUFTRAG J5: eine Zeile pro Person — Tooltime-Email ↔ Pahl-Klarname
// im Backend gematcht; quelle steuert die Badges "nur Tooltime"/"nur Lohn".
export interface TeamPerson {
  anzeige_name: string;
  lohn_name: string | null;
  tooltime_name: string | null;
  quelle: "beide" | "nur_lohn" | "nur_tooltime";
  brutto_letzter_monat_eur: string | null;
  erfasste_stunden_mtd: string | null;
  termine_14d_count: number | null;
  aktiv: boolean | null;
}

export interface TeamResponse {
  firma: string;
  lohn_summen: TeamLohnSummen | null;
  top_5_einzelposten: TeamTopEinzelposten[];
  mitarbeiter_count: number;
  mitarbeiter_aktiv: number;
  data_freshness_iso: string | null;
  empty_reason: "no_lohn_data" | "no_active_employees" | null;
  lohn_stand_label?: string | null;
  lohn_stale?: boolean;
  tooltime_team?: TooltimeTeamBlock | null;
  tooltime_snapshot_iso?: string | null;
  tooltime_stale?: boolean;
  personen?: TeamPerson[];
}

export const fetchTeam = (firma: HoldingFirma) =>
  apiFetch<TeamResponse>(`/api/v1/team/${firma}`);

// ---- Pipeline-Frische (Phase 3 Sanierung: datenfrische_watch) ----

export interface FreshnessItem {
  datenart: string;
  firma: string | null;
  label: string;
  status: "ok" | "stale" | "missing" | "unbekannt";
  letzter_stand_iso: string | null;
  alter_tage: number | null;
  schwelle_tage: number;
  hinweis: string;
  detail: string | null;
  seit: string | null;
}

export interface FreshnessResponse {
  available: boolean;
  last_run_iso: string | null;
  n_nicht_ok: number;
  items: FreshnessItem[];
  hint: string | null;
}

export const fetchPipelinesFreshness = () =>
  apiFetch<FreshnessResponse>("/api/v1/pipelines/freshness");

export function formatDaysSince(days: number): string {
  if (days < 365) return `${days}d`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days - years * 365) / 30);
  return months === 0 ? `${years} J` : `${years} J ${months} Mo`;
}

// ---- InfraX-Liquiditaetsplanung (R1-Datenraum, /cockpit/infrax) ----
// Betraege kommen als Decimal-Strings (pydantic v2 JSON-Mode) — formatEUR-ready.

export interface InfraxLiquiKpis {
  offen_gesamt: string;
  offen_count: number;
  ueberfaellig: string;
  diese_woche: string;
  naechste_woche: string;
  drei_wochen_kum: string;
  dreissig_tage: string;
}

export interface InfraxLiquiFaelligkeit {
  datum: string;
  wochentag: string;
  betrag: string;
  re_nr: string | null;
  auftraggeber: string | null;
}

export interface InfraxLiquiDatenluecke {
  re_nr: string | null;
  betrag: string;
  auftraggeber: string | null;
}

export interface InfraxLiquiSkontoFrist {
  re_nr: string | null;
  skonto_bis: string;
  tage_bis: number;
  betrag_plan: string;
  auftraggeber: string | null;
}

export interface InfraxLiquiWoche {
  woche_ab_mo: string;
  kw: number;
  faellig_summe: string;
  kumuliert: string;
}

export interface InfraxLiquiTag {
  datum: string;
  wochentag: string;
  summe: string;
}

export interface InfraxLiquiStand {
  snapshot_datum: string;
  import_ts: string;
}

export interface InfraxLiquiOverview {
  firma_id: string;
  leer: boolean;
  stale: boolean;
  snapshot_alter_tage: number | null;
  stand: InfraxLiquiStand | null;
  kpis: InfraxLiquiKpis;
  wochen: InfraxLiquiWoche[];
  tage: InfraxLiquiTag[];
  naechste_faelligkeiten: InfraxLiquiFaelligkeit[];
  datenluecken: InfraxLiquiDatenluecke[];
  skonto_fristen: InfraxLiquiSkontoFrist[];
}

export interface InfraxLiquiImportTrigger {
  enqueued: boolean;
  job_id: string;
  queued_at: string;
}

export const fetchInfraxLiquiOverview = () =>
  apiFetch<InfraxLiquiOverview>("/api/v1/infrax-liqui/overview");

export const triggerInfraxLiquiImport = () =>
  apiFetch<InfraxLiquiImportTrigger>("/api/v1/infrax-liqui/import", { method: "POST" });

// ---- Cowork-Task-Runs (Automatisierung-Panel) -----------------------
// Cowork-Schedules schreiben pro Lauf eine task-run-*.json; der Watcher
// (scripts/tooltime_watcher.ps1) POSTet sie an /api/v1/task-runs. Das
// Cockpit liest je task_id den juengsten Lauf fuers Panel.

export type TaskRunStatus = "ok" | "partial" | "skip" | "abort";

export interface CoworkTaskRunRecent {
  task_id: string;
  run_iso: string;
  status: TaskRunStatus;
  summary: string;
  firma_id: HoldingFirma | null;
  source: string;
  details: Record<string, unknown> | null;
  age_minutes: number | null;
}

export interface CoworkTaskRunsRecentResponse {
  available: boolean;
  generated_at: string | null;
  n_tasks: number;
  tasks: CoworkTaskRunRecent[];
  setup_hint: string | null;
}

export const fetchTaskRunsRecent = (limit = 50) =>
  apiFetch<CoworkTaskRunsRecentResponse>(
    `/api/v1/task-runs/recent?limit=${limit}`,
  );
